import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { notesApi } from '../../lib/api';
import { formatDate } from '../../lib/utils';

export const STANCES = [
  { id: 'context', label: 'Add context', shown: 'Your context' },
  { id: 'disagree', label: 'I disagree', shown: 'You disagree' },
  { id: 'untrue', label: 'This isn’t true', shown: 'You say this isn’t true' },
];
const stanceShown = (id) => STANCES.find((s) => s.id === id)?.shown;

const StancePicker = ({ value, onChange, testId }) => (
  <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="What kind of note is this?">
    {STANCES.map((s) => (
      <button
        key={s.id}
        type="button"
        role="radio"
        aria-checked={value === s.id}
        onClick={() => onChange(value === s.id ? null : s.id)}
        className={`rounded-md px-3 py-1.5 text-sm font-medium border ${value === s.id ? 'bg-brand-wash border-brand text-brand-ink' : 'bg-surface border-rule text-ink-70 hover:bg-inset'}`}
        data-testid={`${testId}-stance-${s.id}`}
      >
        {s.label}
      </button>
    ))}
  </div>
);

/**
 * The person's own notes on one thing — a record, a highlight, a finding,
 * an organisation, a placement or a person — oldest first, with a box to
 * add another. `kind` is "comment" (with an optional stance when
 * `withStance`) or "memory" ("What you remember"). Their words sit beside
 * the record's, never over it.
 *
 * Pass `notes` + `onChanged` to show an already-loaded list (the record view
 * loads a record's notes in one go); otherwise it loads its own.
 */
export const NotesThread = ({
  targetType, targetId, orgId, personName, kind = 'comment', withStance = false,
  heading, prompt, placeholder = 'Write it in your own words…', notes: given, onChanged, compact = false, testId = 'notes-thread',
}) => {
  const [own, setOwn] = useState(null);
  const [locked, setLocked] = useState(false);
  const [draft, setDraft] = useState('');
  const [stance, setStance] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);
  const notes = given ?? own;

  const load = useCallback(async () => {
    if (given) return;
    try {
      const r = await notesApi.list({
        target_type: targetType, target_id: targetId || null, org_id: orgId || null, person_name: personName || null, kind,
      });
      if (r.data?.locked) { setLocked(true); setOwn([]); return; }
      setOwn(r.data?.notes || []);
    } catch {
      setOwn([]);
    }
  }, [given, targetType, targetId, orgId, personName, kind]);

  useEffect(() => { load(); }, [load]);

  const changed = () => { if (onChanged) onChanged(); else load(); };

  const add = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await notesApi.create({
        kind, target_type: targetType, target_id: targetId || null, org_id: orgId || null,
        person_name: personName || null, text: draft.trim(), stance: withStance ? stance : null,
      });
      setDraft('');
      setStance(null);
      changed();
    } catch (e) {
      setError(e?.response?.data?.detail || 'That didn’t save — try again.');
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editing?.text?.trim()) return;
    setBusy(true);
    try {
      await notesApi.update(editing.id, { text: editing.text.trim(), stance: editing.stance, person_name: editing.person_name || null });
      setEditing(null);
      changed();
    } catch (e) {
      setError(e?.response?.data?.detail || 'That didn’t save — try again.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      await notesApi.remove(id);
      changed();
    } finally {
      setBusy(false);
    }
  };

  const list = (notes || []).filter((n) => n.kind === kind);

  return (
    <section className="space-y-3" data-testid={testId}>
      {heading && <h3 className={`${compact ? 'text-[15px]' : 'text-[17px]'} font-semibold text-ink`}>{heading}</h3>}
      {prompt && list.length === 0 && <p className="text-[15px] text-ink-55 leading-relaxed">{prompt}</p>}

      {list.length > 0 && (
        <ul className="space-y-2">
          {list.map((n) => (
            <li key={n.id} className="rounded-lg bg-ground p-3 space-y-1" data-testid={`${testId}-note`}>
              {editing?.id === n.id ? (
                <div className="space-y-2">
                  {withStance && <StancePicker value={editing.stance} onChange={(v) => setEditing({ ...editing, stance: v })} testId={`${testId}-edit`} />}
                  <Textarea value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} aria-label="Your note" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveEdit} disabled={busy}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="pd-mono text-ink-55">
                    {formatDate(n.created_at)}
                    {stanceShown(n.stance) && <> · {stanceShown(n.stance)}</>}
                    {n.by_invitee && <> · added by someone you share with</>}
                  </p>
                  <p className="text-[16px] text-ink leading-relaxed whitespace-pre-wrap">{n.text}</p>
                  <div className="flex gap-3">
                    <button type="button" className="text-sm text-ink-55 hover:text-ink" onClick={() => setEditing({ ...n })}>Edit</button>
                    <button type="button" className="text-sm text-ink-55 hover:text-ink" onClick={() => remove(n.id)} disabled={busy} data-testid={`${testId}-delete`}>Delete</button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {locked ? (
        <p className="text-sm text-ink-55">Unlock your account to see and add your notes.</p>
      ) : (
        <div className="space-y-2">
          {withStance && <StancePicker value={stance} onChange={setStance} testId={testId} />}
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            aria-label={heading || 'Your note'}
            className={compact ? 'min-h-[64px]' : 'min-h-[88px]'}
            data-testid={`${testId}-input`}
          />
          {error && <p className="text-sm text-breach-ink">{error}</p>}
          <Button size="sm" variant="secondary" onClick={add} disabled={busy || !draft.trim()} data-testid={`${testId}-add`}>
            {kind === 'memory' ? 'Save what you remember' : 'Add your note'}
          </Button>
        </div>
      )}
    </section>
  );
};

export default NotesThread;
